import { useState, useEffect } from 'react';
import * as api from '../../api/adminApi';
import { PageHeader, cardStyle, sectionTitleStyle, LoadingState, ErrorState } from './shared';

type Settings = Awaited<ReturnType<typeof api.getSettings>>;

const labelStyle = { fontSize: 13, color: '#8B7D6B', marginBottom: 6 } as const;
const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  fontSize: 14,
  border: '1px solid #E8E0D8',
  borderRadius: 8,
  backgroundColor: '#FAFAF8',
  color: '#1A1714',
  outline: 'none',
  fontFamily: '"Geist", system-ui, sans-serif',
} as const;

const fieldGroup = { marginBottom: 16 } as const;

function StatusDot({ ok, label }: { ok: boolean; label?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: ok ? '#22883F' : '#D44A3A' }} />
      <span style={{ fontSize: 13, color: ok ? '#22883F' : '#D44A3A' }}>{label ?? (ok ? 'Connected' : 'Not configured')}</span>
    </div>
  );
}

export default function SettingsPage() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [purging, setPurging] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const s = await api.getSettings();
      setSettings(s);
    } catch (err: unknown) { setError((err as Error).message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handlePurge = async () => {
    if (!confirm('Delete all trashed documents permanently? This cannot be undone.')) return;
    setPurging(true);
    try {
      const result = await api.purgeTrash();
      alert(`Purged ${result.deleted} trashed documents.`);
    } catch (err: unknown) { alert((err as Error).message); }
    finally { setPurging(false); }
  };

  const handleReset = async () => {
    if (!confirm('Reset all usage counters for the current month? This cannot be undone.')) return;
    setResetting(true);
    try {
      const result = await api.resetUsage();
      alert(`Usage counters reset for ${result.month}.`);
    } catch (err: unknown) { alert((err as Error).message); }
    finally { setResetting(false); }
  };

  if (loading && !settings) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const env = settings!.environment;
  const auth = settings!.auth;
  const freeLimits = settings!.planLimits.free;
  const proLimits = settings!.planLimits.pro;
  const storageName = env.storage ? env.storage.charAt(0).toUpperCase() + env.storage.slice(1) : 'Local';

  return (
    <div>
      <PageHeader title="Settings" subtitle="Platform configuration & environment (read-only)" />

      <div style={{ display: 'flex', gap: 24 }}>
        {/* Left column */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* General */}
          <div style={cardStyle}>
            <div style={{ ...sectionTitleStyle, marginBottom: 16 }}>General</div>
            <div style={fieldGroup}>
              <div style={labelStyle}>App Name</div>
              <input style={inputStyle} value={settings!.general.appName} readOnly />
            </div>
            <div>
              <div style={labelStyle}>Client URL</div>
              <input style={inputStyle} value={settings!.general.clientUrl || '—'} readOnly />
            </div>
          </div>

          {/* Plan Limits */}
          <div style={cardStyle}>
            <div style={{ ...sectionTitleStyle, marginBottom: 16 }}>Plan Limits</div>

            <div style={{ fontSize: 12, fontWeight: 600, color: '#8B7D6B', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Free Tier</div>
            <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <div style={labelStyle}>AI Conversations</div>
                <input style={inputStyle} value={freeLimits.aiConversations} readOnly />
              </div>
              <div style={{ flex: 1 }}>
                <div style={labelStyle}>File Operations</div>
                <input style={inputStyle} value={freeLimits.fileOperations} readOnly />
              </div>
              <div style={{ flex: 1 }}>
                <div style={labelStyle}>E-Sign Docs</div>
                <input style={inputStyle} value={freeLimits.esignDocuments} readOnly />
              </div>
            </div>

            <div style={{ fontSize: 12, fontWeight: 600, color: '#8B7D6B', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Pro / Enterprise</div>
            <div style={{ display: 'flex', gap: 16 }}>
              <div style={{ flex: 1 }}>
                <div style={labelStyle}>AI Conversations</div>
                <input style={inputStyle} value={proLimits.aiConversations ?? 'Unlimited'} readOnly />
              </div>
              <div style={{ flex: 1 }}>
                <div style={labelStyle}>Files per Conversation</div>
                <input style={inputStyle} value={proLimits.filesPerConversation} readOnly />
              </div>
              <div style={{ flex: 1 }}>
                <div style={labelStyle}>Max File Size</div>
                <input style={inputStyle} value={`${proLimits.maxFileSizeMB} MB`} readOnly />
              </div>
            </div>
          </div>

          {/* Stripe */}
          <div style={cardStyle}>
            <div style={{ ...sectionTitleStyle, marginBottom: 16 }}>Stripe Billing</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: '#1A1714' }}>Stripe API</span>
              <StatusDot ok={env.stripe} label={env.stripe ? 'Connected' : 'Not configured'} />
            </div>
          </div>
        </div>

        {/* Right column */}
        <div style={{ width: 320, display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Environment */}
          <div style={cardStyle}>
            <div style={{ ...sectionTitleStyle, marginBottom: 16 }}>Environment</div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {[
                { label: 'MongoDB', node: <StatusDot ok={env.mongodb} /> },
                { label: 'Redis', node: <StatusDot ok={env.redis} /> },
                { label: 'Stripe', node: <StatusDot ok={env.stripe} label={env.stripe ? 'Live mode' : 'Not configured'} /> },
                { label: 'Postmark', node: <StatusDot ok={env.postmark} label={env.postmark ? 'Active' : 'Not configured'} /> },
                { label: 'Object Storage', node: <StatusDot ok={true} label={storageName} /> },
                { label: 'Google OAuth', node: <StatusDot ok={env.googleOAuth} /> },
              ].map((item, i, arr) => (
                <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: i < arr.length - 1 ? '1px solid #F0EBE4' : 'none' }}>
                  <span style={{ fontSize: 13, color: '#1A1714' }}>{item.label}</span>
                  {item.node}
                </div>
              ))}
            </div>
          </div>

          {/* Authentication */}
          <div style={cardStyle}>
            <div style={{ ...sectionTitleStyle, marginBottom: 16 }}>Authentication</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <div style={labelStyle}>Access Token TTL</div>
                <input style={inputStyle} value={auth.accessTokenTtl || '—'} readOnly />
              </div>
              <div>
                <div style={labelStyle}>Refresh Token TTL</div>
                <input style={inputStyle} value={auth.refreshTokenTtl || '—'} readOnly />
              </div>
            </div>
          </div>

          {/* Danger Zone */}
          <div style={{ ...cardStyle, border: '1px solid #F5D0C5' }}>
            <div style={{ ...sectionTitleStyle, color: '#D44A3A', marginBottom: 16 }}>Danger Zone</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: '#1A1714' }}>Purge trashed documents</div>
                  <div style={{ fontSize: 12, color: '#8B7D6B' }}>Delete all items in trash immediately</div>
                </div>
                <button
                  onClick={handlePurge}
                  disabled={purging}
                  style={{ padding: '6px 16px', fontSize: 13, fontWeight: 500, backgroundColor: '#fff', color: '#D44A3A', border: '1px solid #D44A3A', borderRadius: 8, cursor: 'pointer', opacity: purging ? 0.6 : 1, fontFamily: '"Geist", system-ui, sans-serif' }}
                >
                  {purging ? 'Purging…' : 'Purge'}
                </button>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: '#1A1714' }}>Reset all usage counters</div>
                  <div style={{ fontSize: 12, color: '#8B7D6B' }}>Clear usage ledger for all users this month</div>
                </div>
                <button
                  onClick={handleReset}
                  disabled={resetting}
                  style={{ padding: '6px 16px', fontSize: 13, fontWeight: 500, backgroundColor: '#fff', color: '#D44A3A', border: '1px solid #D44A3A', borderRadius: 8, cursor: 'pointer', opacity: resetting ? 0.6 : 1, fontFamily: '"Geist", system-ui, sans-serif' }}
                >
                  {resetting ? 'Resetting…' : 'Reset'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
